'use client';

import clsx from 'clsx';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import styles from './Button.module.scss';

export type ButtonVariant =
  | 'primary'
  | 'primary-outlined'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonColor = 'primary' | 'black' | 'white' | 'blue';

import { COLOR_CLASS_MAP } from './Button.constants';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Color override for CMS-driven variants */
  color?: ButtonColor;
  /** Show underline decoration */
  underline?: boolean;
  /** Apply uppercase text-transform (default: true for buttons, false for link variant) */
  uppercase?: boolean;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Icon to display before the label */
  iconBefore?: ReactNode;
  /** Icon to display after the label */
  iconAfter?: ReactNode;
  /** Accessible label (required if children is only an icon) */
  'aria-label'?: string;
}

/**
 * Button component with multiple variants and sizes.
 * Supports loading states and icon positioning.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      color,
      underline: underlineProp,
      uppercase: uppercaseProp,
      fullWidth = false,
      loading = false,
      disabled,
      iconBefore,
      iconAfter,
      className,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isUppercase = uppercaseProp ?? variant !== 'link';
    const colorClass = color && COLOR_CLASS_MAP[color];

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={clsx(
          styles.button,
          styles[variant],
          styles[size],
          colorClass && styles[colorClass],
          underlineProp && styles.underline,
          !isUppercase && styles.noUppercase,
          {
            [styles.fullWidth]: fullWidth,
            [styles.loading]: loading,
            [styles.iconOnly]: !children && (iconBefore || iconAfter),
          },
          className
        )}
        {...props}
      >
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" className={styles.spinnerIcon}>
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="31.4 31.4"
              />
            </svg>
          </span>
        )}
        {iconBefore && !loading && (
          <span className={styles.iconBefore} aria-hidden="true">
            {iconBefore}
          </span>
        )}
        {children && (
          <span
            className={styles.label}
            data-label={typeof children === 'string' ? children : undefined}
          >
            <span className={styles.labelText}>{children}</span>
          </span>
        )}
        {iconAfter && (
          <span className={styles.iconAfter} aria-hidden="true">
            {iconAfter}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
